import { MouseEvent, useId, useState } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import { GENDERS_BY_NAME } from 'src/common/constants/genders';
import dayjs from 'src/helpers/dayjs';
import { IMergeCandidate } from 'src/interfaces/MergeCandidate';

type Props = {
  candidate: IMergeCandidate;
  selected: boolean;
  contactMatch: boolean;
  onToggle: (id: string) => void;
};

export default function MergeCandidateCard({
  candidate,
  selected,
  contactMatch,
  onToggle,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const moreInfoId = useId();
  const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

  const fullName = `${candidate.lastName} ${candidate.firstName}`;
  const editionYears = (candidate.editions ?? [])
    .map(ed => ed.year)
    .filter((y): y is number => y != null)
    .sort((a, b) => b - a);
  const owned = Boolean(candidate.ownerUserId);
  const infoOpen = Boolean(infoAnchor);

  const handleInfoClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInfoAnchor(event.currentTarget);
  };

  return (
    <>
      <TicketCard
        selected={selected}
        sx={
          contactMatch && !selected
            ? {
                bgcolor: alpha(theme.palette.warning.main, 0.14),
                borderColor: 'warning.main',
                borderWidth: 2,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.2)}`,
                opacity: 1,
              }
            : undefined
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, pr: 4 }}>
            <Checkbox
              checked={selected}
              onClick={e => e.stopPropagation()}
              onChange={() => onToggle(candidate.id)}
              size="small"
              sx={{ p: 0.25, mt: 0.15 }}
              slotProps={{
                input: {
                  'aria-label': t('participants.merge.selectAria', { name: fullName }),
                },
              }}
            />
            <Box component="span" sx={{ minWidth: 0 }}>
              {fullName}
            </Box>
          </Box>
        }
        description={
          <Typography variant="body2" component="span" sx={{ display: 'block' }}>
            {candidate.phoneNumber || t('participants.merge.noPhone')}
          </Typography>
        }
        actions={
          <IconButton
            aria-describedby={moreInfoId}
            aria-label={t('participants.merge.infoAria')}
            onClick={handleInfoClick}
            size="small"
            sx={{ color: 'secondary.main' }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        }
        onClick={() => onToggle(candidate.id)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(candidate.id);
          }
        }}
      />

      <Popover
        id={moreInfoId}
        open={infoOpen}
        anchorEl={infoAnchor}
        onClose={() => setInfoAnchor(null)}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: { width: 350, p: 2 },
          },
        }}
      >
        <Grid container spacing={1} columnSpacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.email')}:
            </Typography>
            <Typography variant="body2">{candidate.email}</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.city')}:
            </Typography>
            <Typography variant="body2">{candidate.city}</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('form.country')}:
            </Typography>
            <Typography variant="body2">{candidate.country}</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.gender')}:
            </Typography>
            <Typography variant="body2">
              {t(`base.gender.${GENDERS_BY_NAME[candidate.gender]?.name ?? candidate.gender}`)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.dateOfBirth')}:
            </Typography>
            <Typography variant="body2">
              {candidate.dateOfBirth
                ? dayjs(candidate.dateOfBirth).format('YYYY-MM-DD')
                : '—'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.merge.ownership')}:
            </Typography>
            <Typography variant="body2">
              {owned ? t('participants.merge.owned') : t('participants.merge.unclaimed')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color={grey[500]}>
              {t('participants.merge.editions')}:
            </Typography>
            <Typography variant="body2">
              {editionYears.length
                ? editionYears.join(', ')
                : t('participants.merge.noEditions')}
            </Typography>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
}
